import React, { useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { V2ContextBar } from '../components/V2ContextBar';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/Button';
import { SimpleSelect } from '../components/SimpleSelect';
import { SearchInput } from '../components/SearchInput';
import { useV2Filters } from '../context/V2FiltersContext';
import {
  useDistributionData,
  useDistributionRaw
} from 'apiTyped/hooks/distribution.query';
import { useUserMe } from 'apiTyped/hooks/users.query';
import { getAuthToken } from 'actions/auth';
import getEnvVariable from 'utils/env';
import { formatParam } from 'utils/formatParam';

/**
 * V2 Raw Data page.
 *
 * Uses the V2 context bar for network (channel) and market.
 * Resolves the latest campaign_id matching those context filters,
 * then calls GET /distribution/{campaign_id}/raw.
 */

interface SurveyOption {
  campaignId: number;
  label: string;
  year: number;
  month: number;
  continuous_mode: boolean;
}

export const V2RawData: React.FC = () => {
  const { filters, isLoading: isLoadingContext } = useV2Filters();
  const { isLoadingDistribution, distributionData } = useDistributionData();
  const { user } = useUserMe();
  const isBranded = user?.isBranded;

  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    null
  );
  const [search, setSearch] = useState('');

  const distribConfig = distributionData as Record<string, any>;
  const allCampaigns: any[] = distribConfig?.campaigns || [];

  // Map context channel to network code used in campaign data
  const networkCode = filters.channel === 'on_trade' ? 'on' : 'off';

  // Filter campaigns matching context (network + market)
  const matchingCampaigns = useMemo(() => {
    // First: filter by market + human_validation
    const marketCampaigns = allCampaigns.filter((c) => {
      const matchesMarket =
        !filters.market || c.market?.name === filters.market;
      const isHumanValidated = c.market?.human_validation !== false;
      return matchesMarket && isHumanValidated;
    });

    // Then: try to match network
    const networkFiltered = marketCampaigns.filter((c) => {
      const code = c.network?.code;
      return (
        code === networkCode ||
        code === `${networkCode}_trade` ||
        (networkCode === 'on' && code === 'on_trade') ||
        (networkCode === 'off' && code === 'off_trade')
      );
    });

    // Use network-filtered if available, otherwise fall back to all for that market
    const candidates =
      networkFiltered.length > 0 ? networkFiltered : marketCampaigns;

    // Filter to those with wine_survey (or spirit_survey if no wine ones)
    const withWine = candidates.filter((c) => c.wine_survey);
    return withWine.length > 0 ? withWine : candidates;
  }, [allCampaigns, networkCode, filters.market]);

  // Build survey options sorted with latest first
  const surveyOptions = useMemo<SurveyOption[]>(() => {
    return matchingCampaigns
      .map((c) => ({
        campaignId: c.id,
        label: c.continuous_mode
          ? 'Current'
          : `${moment(c.month, 'M').format('MMM')} ${c.year}`,
        year: c.year,
        month: c.month,
        continuous_mode: c.continuous_mode
      }))
      .sort((a, b) => {
        if (a.continuous_mode) return -1;
        if (b.continuous_mode) return 1;
        return b.year - a.year || b.month - a.month;
      });
  }, [matchingCampaigns]);

  // Default to latest campaign; reset when context changes
  const effectiveCampaignId = useMemo(() => {
    if (
      selectedCampaignId &&
      surveyOptions.find((s) => s.campaignId === selectedCampaignId)
    ) {
      return selectedCampaignId;
    }
    return surveyOptions.length > 0 ? surveyOptions[0].campaignId : null;
  }, [selectedCampaignId, surveyOptions]);

  // Resolve wines/brands from distribution data for the query
  const clientItems = useMemo(() => {
    const wines: any[] = distribConfig?.client_wines_distri || [];
    const brands: any[] = distribConfig?.client_marques_distri || [];
    if (isBranded) {
      return brands
        .filter(
          (b: any) =>
            b.product_type === 'wine' || b.product_type === 'fortified wine'
        )
        .map((b: any) => b.marque_name);
    }
    return wines
      .filter(
        (w: any) =>
          w.product_type === 'wine' || w.product_type === 'fortified wine'
      )
      .map((w: any) => w.wine_name);
  }, [distribConfig, isBranded]);

  // Build query options (like v1)
  const queryOptions = useMemo(() => {
    if (!clientItems.length) return {};
    return isBranded
      ? { brands: clientItems, wine_type: 'wine', search: search || undefined }
      : { wines: clientItems, wine_type: 'wine', search: search || undefined };
  }, [clientItems, isBranded, search]);

  // Fetch raw data using v1 endpoint
  const { isLoading, data } = useDistributionRaw(
    effectiveCampaignId || 0,
    queryOptions,
    !!(effectiveCampaignId && clientItems.length)
  );

  // Format data (merge current + last_year like v1)
  const formattedData = useMemo(() => {
    const current = (data as any)?.current || [];
    const lastYear = ((data as any)?.last_year || []).map((item: any) => ({
      ...item,
      lastYear: true
    }));
    return [...current, ...lastYear];
  }, [data]);

  // Client-side search filtering
  const filteredData = useMemo(() => {
    if (!search) return formattedData;
    const term = search.toLowerCase();
    return formattedData.filter(
      (row: any) =>
        (row.store || '').toLowerCase().includes(term) ||
        (row.wine_name || '').toLowerCase().includes(term)
    );
  }, [formattedData, search]);

  // Export handler (v1 style: /distribution/{campaign_id}/raw/xls)
  const handleExport = useCallback(() => {
    if (!effectiveCampaignId) return;
    const token = getAuthToken();
    const baseUrl =
      getEnvVariable('REACT_APP_TYPED_API_URL') ||
      getEnvVariable('REACT_APP_API_URL');

    const params = new URLSearchParams();
    params.set('no_page', '1');
    if (isBranded) {
      params.set('brands', formatParam(JSON.stringify(clientItems)));
    } else {
      params.set('wines', formatParam(JSON.stringify(clientItems)));
    }
    params.set('wine_type', 'wine');
    if (search) params.set('search', search);

    const url = `${baseUrl}/distribution/${effectiveCampaignId}/raw/xls?${params.toString()}`;
    window.open(`${url}&token=${token}`, '_blank');
  }, [effectiveCampaignId, clientItems, isBranded, search]);

  // Format price helper
  const formatPrice = (value: number | null, currency?: string) => {
    if (value == null) return '—';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Table columns matching v1 raw data
  const columns = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        sortable: true,
        width: '8%',
        render: (row: any) => row.date || '—'
      },
      {
        key: 'store',
        header: 'Point of Sale',
        sortable: true,
        width: '20%',
        render: (row: any) => (
          <PosName>
            <span>{row.store || '—'}</span>
            {row.lastYear && <YearBadge>N-1</YearBadge>}
          </PosName>
        )
      },
      {
        key: 'wine_name',
        header: 'Wine',
        sortable: true,
        width: '20%',
        render: (row: any) => row.wine_name || '—'
      },
      {
        key: 'vintage',
        header: 'Vintage',
        sortable: true,
        width: '8%',
        render: (row: any) => row.vintage || '—'
      },
      {
        key: 'format',
        header: 'Format',
        sortable: true,
        width: '10%',
        render: (row: any) => {
          if (!row.format) return '—';
          if (!isNaN(parseInt(row.format)))
            return `${Number(row.format).toLocaleString('fr-FR')} ml`;
          return row.format;
        }
      },
      {
        key: 'local_price',
        header: 'Local Price',
        sortable: true,
        width: '10%',
        render: (row: any) => formatPrice(row.local_price, row.local_currency)
      },
      {
        key: 'price',
        header: 'Price',
        sortable: true,
        width: '10%',
        render: (row: any) => formatPrice(row.price, row.displayed_currency)
      },
      {
        key: 'evolution',
        header: 'Evolution',
        sortable: true,
        width: '10%',
        render: (row: any) => {
          if (row.evolution == null) return '—';
          return row.evolution;
        }
      }
    ],
    []
  );

  const isPageLoading = isLoadingContext || isLoadingDistribution || isLoading;

  return (
    <Page>
      <PageHeader
        title="Raw Data"
        subtitle="Drill into the underlying competitor and POS data feeding the synthesis pages — raw, exportable."
      />
      <V2ContextBar />

      {/* Card wrapping the table — survey + search + export inline like v1 */}
      <Card>
        <CardToolbar>
          <ToolbarLeft>
            {filters.market && <MarketLabel>{filters.market}</MarketLabel>}
            {!isPageLoading && (
              <RecordCount>
                {filteredData.length} record
                {filteredData.length !== 1 ? 's' : ''}
              </RecordCount>
            )}
          </ToolbarLeft>
          <ToolbarRight>
            <FilterGroup>
              <FilterLabel>Survey</FilterLabel>
              <SimpleSelect
                options={surveyOptions.map((s) => ({
                  value: s.campaignId,
                  label: s.label
                }))}
                value={effectiveCampaignId ?? ''}
                onChange={(val) => setSelectedCampaignId(val as number)}
                disabled={surveyOptions.length === 0}
                minWidth="100px"
              />
            </FilterGroup>
            <SearchInput
              placeholder="Search wine or POS..."
              value={search}
              onChange={(val) => setSearch(val)}
            />
            <Button
              onClick={handleExport}
              disabled={isPageLoading || filteredData.length === 0}
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              }
            >
              Export XLS
            </Button>
          </ToolbarRight>
        </CardToolbar>

        {isPageLoading ? (
          <LoadingWrap>Loading raw data...</LoadingWrap>
        ) : (
          <DataTable
            columns={columns}
            data={filteredData}
            emptyMessage="No raw data found for the current filters."
          />
        )}
      </Card>
    </Page>
  );
};

// --- Styled ---

const Page = styled.div`
  max-width: 1400px;
`;

const Card = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const CardToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line-soft);
  gap: 12px;
  flex-wrap: wrap;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MarketLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-muted);
  padding-left: 12px;
  border-left: 1px solid var(--line);
`;

const RecordCount = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FilterLabel = styled.span`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: var(--ink-faint);
  white-space: nowrap;
`;

const LoadingWrap = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

const PosName = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
`;

const YearBadge = styled.span`
  font-size: 9px;
  font-weight: 600;
  background: var(--amber-soft);
  color: var(--amber);
  border-radius: 3px;
  padding: 1px 5px;
`;
